import { UserMinus, UserPlus } from 'lucide-react';
import { TenantUserListPhoto } from '../../../features/user/components/UserListPhoto';
import { formatInitials } from '../../../features/user/userDisplay';
import type { ChatMember } from '../../../types/chatMember';

type ChatSessionShareMemberRowProps = {
  member: ChatMember;
  isCurrentUser: boolean;
  isOwner: boolean;
  sessionTitle: string;
  translate: (key: string, params?: Record<string, string>) => string;
  onTransferOwnership: (userId: string) => Promise<void>;
  onUninvite: (userId: string) => Promise<void>;
  onLeave: () => void;
};

export function ChatSessionShareMemberRow({
  member,
  isCurrentUser,
  isOwner,
  sessionTitle,
  translate,
  onTransferOwnership,
  onUninvite,
  onLeave,
}: ChatSessionShareMemberRowProps) {
  return (
    <li
      className={[
        'liks-chat-share__list-item',
        isCurrentUser && 'liks-chat-share__list-item--self',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="liks-chat-share__row">
        <TenantUserListPhoto
          thumbnail={member.thumbnail}
          initials={formatInitials(member.name)}
          className="liks-chat-share__photo"
        />
        <div className="liks-chat-share__person">
          <span className="liks-chat-share__person-name">
            <span className="liks-chat-share__person-name-text">
              {member.name}
            </span>
            {isCurrentUser && (
              <span className="liks-chat-share__you-badge">
                {translate('chatSessions.shareYouBadge')}
              </span>
            )}
            <span className="liks-chat-share__badge">
              {member.chatRole === 'owner'
                ? translate('chatSessions.shareOwnerBadge')
                : translate('chatSessions.shareParticipantBadge')}
            </span>
          </span>
          <span className="liks-chat-share__person-meta">{member.email}</span>
        </div>
      </div>
      <div className="liks-chat-share__person-actions">
        {isOwner && member.chatRole === 'participant' && (
          <>
            <button
              type="button"
              className="liks-chat-share__action liks-chat-share__action--primary"
              aria-label={translate('chatSessions.transferOwnershipAction', {
                name: member.name,
              })}
              onClick={() => {
                void onTransferOwnership(member.userId).catch(() => undefined);
              }}
            >
              <UserPlus strokeWidth={2} aria-hidden />
              <span>{translate('chatSessions.transferOwnership')}</span>
            </button>
            <button
              type="button"
              className="liks-chat-share__action liks-chat-share__action--danger"
              aria-label={translate('chatSessions.shareRemove', {
                name: member.name,
              })}
              onClick={() => {
                void onUninvite(member.userId).catch(() => undefined);
              }}
            >
              <UserMinus strokeWidth={2} aria-hidden />
              <span>{translate('chatSessions.shareRemoveLabel')}</span>
            </button>
          </>
        )}
        {isCurrentUser && member.chatRole === 'participant' && (
          <button
            type="button"
            className="liks-chat-share__action liks-chat-share__action--danger"
            aria-label={translate('chatSessions.shareLeave', {
              title: sessionTitle,
            })}
            onClick={onLeave}
          >
            <UserMinus strokeWidth={2} aria-hidden />
            <span>{translate('chatSessions.shareLeaveLabel')}</span>
          </button>
        )}
      </div>
    </li>
  );
}
